// Standard-Arrays für Video- und Bildformate
let videoFormats = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'm4v'];
let imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'];

// Diese Arrays können überschrieben werden, z.B.:
// videoFormats = ['mp4', 'webm']; // falls nur diese Formate unterstützt werden sollen

export function initVenoBox () {
    // Prüfe ob VenoBox verfügbar ist
    if (typeof VenoBox === 'undefined') {
        console.log('VenoBox nicht verfügbar - keine .venobox Elemente gefunden oder Bibliothek nicht geladen');
        return;
    }

    // Erstelle einen Selektor-String für alle unterstützten Formate
    const allFormats = [...videoFormats, ...imageFormats];
    const selector = allFormats.map(format => `a[href$=".${format}"]`).join(',');

    new VenoBox({
        selector: selector,
        infinigall: true,
        maxWidth: '80%',
        numeration: true,
        spinner: 'flow',
        initialScale: 0.9,
        transitionSpeed: 200,
        fitView: false,
    });
}

const ratioMap = {
    '1x1': 1,
    '4x3': 4 / 3,
    '16x9': 16 / 9,
    '21x9': 21 / 9,
    '9x16': 9 / 16,
    '3x4': 3 / 4
};

const loadVideoMetadata = (videoUrl) => new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const cleanup = () => {
        video.removeAttribute('src');
        video.load();
    };

    video.preload = 'metadata';
    video.muted = true;

    video.addEventListener('loadedmetadata', () => {
        resolve({
            width: video.videoWidth,
            height: video.videoHeight
        });
        cleanup();
    }, {once: true});

    video.addEventListener('error', (event) => {
        reject(event);
        cleanup();
    }, {once: true});

    video.src = videoUrl;
});

const determineAspectRatio = (width, height) => {
    if (!width || !height) {
        return {ratio: '16x9'};
    }

    const computed = width / height;
    let bestMatch = '16x9';
    let minDifference = Infinity;

    Object.entries(ratioMap).forEach(([name, value]) => {
        const difference = Math.abs(computed - value);
        if (difference < minDifference) {
            minDifference = difference;
            bestMatch = name;
        }
    });

    if (minDifference > 0.08) {
        return {
            ratio: 'custom',
            customRatio: `${(height / width * 100).toFixed(2)}%`,
            customMaxWidth: `calc(min(var(--vbox-max-width), (100vh - 60px) * ${width} / ${height}))`
        };
    }

    return {ratio: bestMatch};
};

const videoMetadataTasks = new WeakMap();
let videoMetadataObserver = null;

const getVideoMetadataObserver = () => {
    if (!('IntersectionObserver' in window)) {
        return null;
    }

    if (!videoMetadataObserver) {
        videoMetadataObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    return;
                }

                const task = videoMetadataTasks.get(entry.target);
                if (typeof task === 'function') {
                    task();
                }
                videoMetadataObserver.unobserve(entry.target);
            });
        }, {
            rootMargin: '200px 0px'
        });
    }

    return videoMetadataObserver;
};

const setupLazyVideoMetadata = (link) => {
    if (!link || link.dataset.vsmLightboxBound === 'true') {
        return;
    }

    const videoUrl = link.getAttribute('href');
    if (!videoUrl) {
        return;
    }

    const triggerMetadata = () => {
        if (link.dataset.vsmLightboxReady === 'true' || link.dataset.vsmLightboxLoading === 'true') {
            return;
        }

        link.dataset.vsmLightboxLoading = 'true';

        loadVideoMetadata(videoUrl)
            .then(({width, height}) => {
                const {ratio, customRatio, customMaxWidth} = determineAspectRatio(width, height);
                link.setAttribute('data-ratio', ratio);
                if (ratio === 'custom' && customRatio && customMaxWidth) {
                    link.style.setProperty('--custom-aspect-ratio', customRatio);
                    link.style.setProperty('--custom-max-width', customMaxWidth);
                }
            })
            .catch(() => {
                link.setAttribute('data-ratio', '16x9');
            })
            .finally(() => {
                delete link.dataset.vsmLightboxLoading;
                link.dataset.vsmLightboxReady = 'true';
                videoMetadataTasks.delete(link);
            });
    };

    const pointerHandler = () => {
        triggerMetadata();
    };

    videoMetadataTasks.set(link, triggerMetadata);

    link.addEventListener('pointerenter', pointerHandler, {once: true});
    link.addEventListener('focus', pointerHandler, {once: true});
    link.addEventListener('click', triggerMetadata, {once: true, capture: false});

    const observer = getVideoMetadataObserver();
    if (observer) {
        observer.observe(link);
    }

    link.dataset.vsmLightboxBound = 'true';
};

export function initVideoLightbox() {
    // Prüfe ob VenoBox verfügbar ist
    if (typeof VenoBox === 'undefined') {
        console.log('VenoBox nicht verfügbar - Video-Lightbox wird übersprungen');
        return;
    }

    const videoSelector = videoFormats.map(format => `a[href$=".${format}"]`).join(',');
    const videoLinks = document.querySelectorAll(videoSelector);

    videoLinks.forEach(link => {
        link.setAttribute('data-autoplay', 'true');
        link.setAttribute('data-vbtype', 'video');
        if (!link.hasAttribute('data-ratio')) {
            link.setAttribute('data-ratio', '16x9');
        }

        setupLazyVideoMetadata(link);
    });
}

export function initImageLightbox () {
    // Prüfe ob VenoBox verfügbar ist
    if (typeof VenoBox === 'undefined') {
        console.log('VenoBox nicht verfügbar - Image-Lightbox wird übersprungen');
        return;
    }

    const imageSelector = imageFormats.map(format => `a[href$=".${format}"]`).join(',');
    const imageLinks = document.querySelectorAll(imageSelector);

    imageLinks.forEach(link => {
        // Hier können Sie beliebige Parameter für Bildlinks setzen
        // Beispiel:
        // link.setAttribute('data-gall', 'gallery1');
    });
}
